import { useEffect } from 'react';
import Head from 'next/head';
import { ParameterCod } from '@/constants/parameters';
import { ThemeProvider } from '@material-tailwind/react';
import Cookies from 'js-cookie';

import { PageVersionType, useActivePageStore, useSelectedPageStore } from '@/utils/store/store';

import {
  ActiveVersionQuery,
  useActiveVersionQuery,
} from '../../Providers/GraphqlProvider/generated';
import { useLayoutConfigContext } from '../LayoutConfig/LayoutConfigContext';

interface LayoutAdminMenuProps {
  children: React.ReactNode;
}

export default function LayoutTheme({ children }: LayoutAdminMenuProps) {
  const { save } = useActivePageStore();
  const { save: saveDraft } = useSelectedPageStore();
  const { data, loading } = useActiveVersionQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    context: {
      headers: { mondongo: true },
    },
  });

  const {
    layoutConfig: { parametersDefault },
  } = useLayoutConfigContext();

  useEffect(() => {
    updatePageVersion(data, save, saveDraft);
  }, [data, save, saveDraft]);

  const organizationName = parametersDefault?.find((x) => x.code == ParameterCod.Name)?.character;

  const defaultTitle = organizationName
    ? `${organizationName} - Sede Electrónica`
    : 'Sede Electrónica';

  if (loading) {
    return null;
  }

  return (
    <ThemeProvider
      value={{
        dialog: {
          styles: {
            base: {
              backdrop: {
                zIndex: 'var(--z-overlay)',
              },
              container: {
                zIndex: 'var(--z-overlay)',
              },
            },
          },
        },
      }}
    >
      <Head>
        <title>{defaultTitle}</title>
      </Head>
      {children}
    </ThemeProvider>
  );
}

// export const updatePageVersion = (
//   dataActiveVersion: ActiveVersionQuery | undefined,
//   save: (page: PageVersionType) => void,
//   saveDraft: (page: PageVersionType) => void
// ) => {
//   const pageCache = Cookies.get('page')
//   const pageVersion = JSON.parse(pageCache || '{}')
//   if (dataActiveVersion && dataActiveVersion?.version) {
//     const activeVersion = dataActiveVersion.version
//     console.log('activeVersion', activeVersion)
//     save({
//       id: activeVersion?.id,
//       description: activeVersion?.description || '',
//       state: activeVersion?.state,
//       theme: activeVersion.theme,
//       consecutive: activeVersion.consecutive,
//     })
//     if (pageCache) {
//       saveDraft({
//         id: pageVersion?.id,
//         description: pageVersion?.description,
//         state: pageVersion?.state,
//         theme: pageVersion.theme,
//         consecutive: pageVersion.consecutive,
//       })
//     } else {
//       saveDraft({
//         id: activeVersion?.id,
//         description: activeVersion?.description || '',
//         state: activeVersion?.state,
//         theme: activeVersion.theme,
//         consecutive: activeVersion.consecutive,
//       })
//       if (activeVersion.id) {
//         console.log('activeVersion.id for cookie', JSON.stringify(activeVersion))
//         Cookies.set('page', JSON.stringify(activeVersion))
//       }
//     }
//   }
// }

export const updatePageVersion = (
  dataActiveVersion: ActiveVersionQuery | undefined,
  save: (page: PageVersionType) => void,
  saveDraft: (page: PageVersionType) => void,
  idToDelete?: string,
  currentPage?: string
) => {
  // Obtener la versión guardada en la cookie 'page'
  const pageCache = Cookies.get('page');
  const cachedPageVersion: PageVersionType | null = pageCache ? JSON.parse(pageCache) : null;
  const parsedCurrentPage: PageVersionType | null = currentPage ? JSON.parse(currentPage) : null;

  // Validar que exista una versión activa para continuar
  if (!dataActiveVersion?.version) {
    return;
  }

  const activeVersion = dataActiveVersion.version;

  // Guardar la versión activa
  save({
    id: activeVersion.id,
    description: activeVersion.description || '',
    state: activeVersion.state,
    theme: activeVersion.theme,
    consecutive: activeVersion.consecutive,
  });

  // Si existe una versión cacheada, usarla para guardar el borrador
  if (cachedPageVersion) {
    saveDraft({
      id: cachedPageVersion.id,
      description: cachedPageVersion.description || '',
      state: cachedPageVersion.state,
      theme: cachedPageVersion.theme,
      consecutive: cachedPageVersion.consecutive,
    });
    return;
  }

  // Si no hay cookie, se procede a manejar el borrador según currentPage
  if (parsedCurrentPage && parsedCurrentPage.id !== idToDelete) {
    Cookies.set('page', currentPage || '');
    saveDraft({
      id: parsedCurrentPage.id,
      description: parsedCurrentPage.description || '',
      state: parsedCurrentPage.state,
      theme: parsedCurrentPage.theme,
      consecutive: parsedCurrentPage.consecutive,
    });
  } else if (activeVersion.id) {
    // Caso en el que se utiliza la versión activa para el borrador y se actualiza la cookie
    saveDraft({
      id: activeVersion.id,
      description: activeVersion.description || '',
      state: activeVersion.state,
      theme: activeVersion.theme,
      consecutive: activeVersion.consecutive,
    });
    Cookies.set('page', JSON.stringify(activeVersion));
  }
};
